import React, { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Landing from '@/pages/Landing';
import Home from '@/pages/Home';
import Auth from '@/pages/Auth';
import Profile from '@/pages/Profile';
import BarDetail from '@/pages/BarDetail';
import Friends from '@/pages/Friends';
import BarOwnerAuth from '@/pages/BarOwnerAuth';
import BarOwnerDashboard from '@/pages/BarOwnerDashboard';
import Leaderboard from '@/pages/Leaderboard';
import SocialFeed from '@/pages/SocialFeed';
import Premium from '@/pages/Premium';
import PremiumSuccess from '@/pages/PremiumSuccess';
import Reservations from '@/pages/Reservations';
import Matchmaking from '@/pages/Matchmaking';
import BarCrawl from '@/pages/BarCrawl';
import { Toaster } from '@/components/ui/sonner';
import MatrixBackground from '@/components/MatrixBackground';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

import GoogleBarDetail from './pages/GoogleBarDetail';

export const AuthContext = React.createContext();
export const ThemeContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('barbuzz-dark-mode');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('barbuzz-dark-mode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (e) {
      console.error(e);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  // Refresh user data from server - call this after profile picture upload
  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (e) {
      console.error('Failed to refresh user:', e);
      return null;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <ThemeContext.Provider value={{ darkMode, setDarkMode }}>
      <AuthContext.Provider value={{ user, login, logout, refreshUser }}>
        <div className="App relative min-h-screen">
          {darkMode && <MatrixBackground />}
          <div className="relative z-10">
            <BrowserRouter>
              <Routes>
                <Route path="/" element={user ? <Home /> : <Landing />} />
                <Route path="/auth" element={!user ? <Auth /> : <Navigate to="/home" />} />
                <Route path="/home" element={user ? <Home /> : <Navigate to="/" />} />
                <Route path="/profile" element={user ? <Profile /> : <Navigate to="/" />} />
                <Route path="/bar/:barId" element={user ? <BarDetail /> : <Navigate to="/" />} />
                <Route path="/bar/google/:placeId" element={user ? <GoogleBarDetail /> : <Navigate to="/" />} />
                <Route path="/friends" element={user ? <Friends /> : <Navigate to="/" />} />
                <Route path="/bar-owner/auth" element={<BarOwnerAuth />} />
                <Route path="/bar-owner/dashboard" element={<BarOwnerDashboard />} />
                <Route path="/leaderboard" element={user ? <Leaderboard /> : <Navigate to="/" />} />
                <Route path="/feed" element={user ? <SocialFeed /> : <Navigate to="/" />} />
                <Route path="/premium" element={user ? <Premium /> : <Navigate to="/" />} />
                <Route path="/premium/success" element={user ? <PremiumSuccess /> : <Navigate to="/" />} />
                <Route path="/reservations" element={user ? <Reservations /> : <Navigate to="/" />} />
                <Route path="/matchmaking" element={user ? <Matchmaking /> : <Navigate to="/" />} />
                <Route path="/bar-crawl" element={user ? <BarCrawl /> : <Navigate to="/" />} />
              </Routes>
            </BrowserRouter>
          </div>
          <Toaster />
        </div>
      </AuthContext.Provider>
    </ThemeContext.Provider>
  );
}

export default App;